import * as React from 'react';
import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { useIOSKeyboardOffset } from '@rocco/hooks/use-ios-keyboard-offset';
import { cn } from '../../utils/cn';
import { Dialog } from '../dialog/dialog';
import { DialogContent } from '../dialog/dialog-content';
import { DialogTitle } from '../dialog/dialog-title';
import { Drawer } from '../drawer/drawer';
import { DrawerContent } from '../drawer/drawer-content';
import { DrawerSwipeZone } from '../drawer/drawer-swipe-dismiss';
import { DrawerTitle } from '../drawer/drawer-title';

/**
 * The most of the viewport the sheet may take. The remainder is the slice of
 * the page left visible behind it, which is what makes the sheet feel
 * dismissable rather than like a takeover.
 */
const SHEET_MAX_HEIGHT = '88dvh';

/**
 * The same spring, sampled into a `linear()` curve — but critically damped, so
 * it settles without overshooting.
 *
 * The overshoot was the nicer motion on a desktop and the wrong trade on a
 * phone. Animating height is layout-bound: every frame reflows the step inside
 * the sheet, and where that cannot hold 60fps the settle reads as a wobble
 * rather than as weight. A curve that only ever approaches its target degrades
 * into "slightly slow" instead of "broken" when frames go missing.
 *
 * Regenerate with the critically-damped closed form (zeta = 1):
 *   x(t) = e^(-w0*t) * (1 + w0*t), progress 1-x, w0 = sqrt(stiffness/mass)
 */
const SHEET_SPRING_EASING =
    'linear(0,0.1147,0.3214,0.5173,0.6719,0.7838,0.8607,0.9117,0.9448,0.9659,0.9791,0.9873,0.9923,0.9954,0.9972,0.9983,1)';

/** Where that curve has settled to within a thousandth. */
const SHEET_SPRING_MS = 320;

/**
 * The incoming step catches up over the first stretch of the resize rather than
 * appearing whole against a box that is still moving.
 */
const CONTENT_SETTLE_MS = 260;

/** Quiet time after the last viewport change before the sheet springs again. */
const KEYBOARD_SETTLE_MS = 200;
import type { DialogCloseVariant } from '../dialog/dialog-close';
import {
    TWO_PANE_SURFACE_SIZE_CLASSNAME,
    TwoPaneSurface,
} from './two-pane-surface';
import { ScrollHint } from './scroll-hint';
import { useScrollOverflow } from './use-scroll-overflow';

/**
 * Figma close control: ~22px icon at top~13 / right~16 on the card.
 * Numeric scale is design-px/4 (same as the card width/height steps).
 */
const TWO_PANE_CLOSE_BUTTON_CLASSNAME = 'top-3.25 right-4 [&_svg]:size-5.5';

export interface TwoPaneModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /**
     * Accessible name for the dialog. Rendered for screen readers only — the
     * visible heading belongs inside `children`, where the layout can place it.
     */
    title: string;
    /**
     * Left pane: brand imagery, a checklist, a selection — whatever the surface
     * is about. Desktop only; on mobile the surface is a bottom sheet holding
     * `children` alone, so anything the reader must not lose belongs in
     * `children` as well.
     *
     * When set, wins over `paneImage` / `paneStatement`.
     */
    pane?: React.ReactNode;
    /**
     * Cover photo for the built-in brand pane. Used only when `pane` is omitted.
     * Renders the ArchiPro receipt mark over the image; pair with `paneStatement`
     * for bottom-left copy.
     */
    paneImage?: string;
    /**
     * White statement at the bottom of the brand photo. Ignored unless
     * `paneImage` is set (and `pane` is omitted).
     */
    paneStatement?: string;
    /** Right pane: the form, steps, or whatever the reader acts on. */
    children: React.ReactNode;
    /**
     * Vertical padding of the right pane. `roomy` suits a single centred step.
     * Desktop only — it exists to centre a short step inside the fixed-height
     * card, and the sheet has no fixed height to centre within.
     */
    bodyPadding?: 'default' | 'roomy';
    /**
     * Card and left-pane corner radius. Defaults to `square`; the auth surfaces
     * and the feedback modals all pass `rounded`, so a square caller is opting
     * out rather than taking a house style.
     */
    corners?: 'square' | 'rounded';
    /**
     * Lifts the scroll hint off the bottom edge, for a step that pins its own
     * action there. Without it the hint sits behind that bar, which is opaque.
     */
    scrollHintClassName?: string;
    /**
     * Step key of a multi-step surface. Each change rewinds the scroll and
     * re-arms the scroll hint — see {@link useScrollOverflow}.
     */
    scrollResetKey?: string;
    closeButton?: boolean;
    /**
     * Block dismissal on the sheet — swipe, outside tap and escape. The desktop
     * card uses `onPointerDownOutside` for the same purpose; the sheet needs its
     * own signal because a swipe never reaches that handler.
     */
    dismissible?: boolean;
    /** Visual style for the built-in close control. */
    closeButtonVariant?: DialogCloseVariant;
    /** Extra classes for the close control; merged after the two-pane defaults. */
    closeButtonClassName?: string;
    className?: string;
    paneClassName?: string;
    bodyClassName?: string;
    /** Applied to the sheet surface on mobile. */
    sheetClassName?: string;
    /** Block dismissal — e.g. while a submit is in flight. */
    onPointerDownOutside?: React.ComponentProps<
        typeof DialogContent
    >['onPointerDownOutside'];
}

/**
 * The shell every gate renders into: a two-pane card on desktop, a bottom sheet
 * on mobile.
 *
 * The split is driven by `isMobile` — the UA-derived display mode, not a
 * viewport query — so a phone held in landscape gets the sheet even though it
 * measures wider than `md`. The desktop card is 635px tall and simply does not
 * fit a landscape phone. Business Manager never seeds the display-mode atom, so
 * it keeps the card.
 */
export const TwoPaneModal = (props: TwoPaneModalProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);

    return isMobile ? <TwoPaneSheet {...props} /> : <TwoPaneCard {...props} />;
};

/**
 * Mobile surface: the card's right pane alone, as a rounded bottom sheet. The
 * left pane is not rendered — a phone has no room for imagery beside the thing
 * the reader came to do.
 */
const TwoPaneSheet = ({
    open,
    onOpenChange,
    title,
    children,
    closeButton = true,
    dismissible = true,
    bodyClassName,
    sheetClassName,
    closeButtonClassName,
    scrollHintClassName,
    scrollResetKey,
    onPointerDownOutside,
}: TwoPaneModalProps) => {
    // On iOS a `fixed; bottom: 0` element is placed against the initial
    // containing block, which does not shrink for the keyboard — so the sheet
    // is lifted by the keyboard's height and its cap reduced to match, rather
    // than being left underneath it.
    // Only while the sheet is up. Every gate mounts closed on every page, so an
    // unconditional listener here is three of them re-rendering on every frame
    // of every scroll, for a number nothing is reading.
    const keyboardOffset = useIOSKeyboardOffset(open);
    const keyboardMoving = useKeyboardMoving(keyboardOffset);
    const { handleRef, bodyRef, contentHeight, animate } =
        useSheetContentHeight(open, keyboardMoving);
    // The sheet builds its own scroll body rather than going through
    // TwoPaneSurface, so it needs its own hint — and it is the surface that
    // most often has more below, being the shorter of the two.
    const scroll = useScrollOverflow<HTMLDivElement>(scrollResetKey);
    // The spring belongs to step changes, which are the sheet's own decision.
    // A keyboard is the reader dragging the bottom of the screen around, and it
    // has its own animation curve: springing at it means every frame of the
    // keyboard's travel restarts the transition from wherever the last one
    // had got to, which is the jitter. Tracking it exactly is what native does.
    const springHeight = animate && !keyboardMoving;

    return (
        <Drawer
            open={open}
            onOpenChange={onOpenChange}
            dismissible={dismissible}
        >
            <DrawerContent
                variant="default"
                noHandle
                hideDefaultCloseButton={!closeButton}
                onPointerDownOutside={onPointerDownOutside}
                className={cn(
                    'bg-white data-[vaul-drawer-direction=bottom]:rounded-t-2xl',
                    // Keeps a height change from invalidating layout beyond the
                    // sheet. Without it the animation makes the page underneath
                    // part of every frame's work, on the surface least able to
                    // afford it.
                    '[contain:layout_paint]',
                    sheetClassName,
                )}
                // The default close button sits at `md:top-7 md:right-6`, and a
                // landscape phone is `md` — inside a sheet that would drop it
                // out of line with the drag handle it shares a row with. The
                // caller's classes merge after, as they do on the card, so the
                // same prop is not silently dropped on one surface.
                closeButtonClassName={cn(
                    'top-4 right-5 md:top-4 md:right-5',
                    closeButtonClassName,
                )}
                style={{
                    // The measured height is what animates; the cap is what
                    // stops a long step from covering the page. A step taller
                    // than the cap scrolls inside it.
                    height: contentHeight ?? undefined,
                    maxHeight: keyboardOffset
                        ? `calc(${SHEET_MAX_HEIGHT} - ${keyboardOffset}px)`
                        : SHEET_MAX_HEIGHT,
                    // Inline rather than a utility because it has to beat the
                    // drawer's own transition shorthand, and because reduced
                    // motion is decided here rather than by a `motion-reduce:`
                    // variant an inline rule would win against anyway.
                    ...(springHeight
                        ? {
                              transitionProperty: 'height',
                              transitionDuration: `${SHEET_SPRING_MS}ms`,
                              transitionTimingFunction: SHEET_SPRING_EASING,
                              willChange: 'height',
                          }
                        : {}),
                    ...(keyboardOffset ? { bottom: keyboardOffset } : {}),
                }}
            >
                <DrawerTitle className="sr-only">{title}</DrawerTitle>
                <div ref={handleRef} className="shrink-0">
                    <DrawerSwipeZone
                        onDismiss={() => onOpenChange(false)}
                        canDismiss={() => dismissible}
                    />
                </div>
                <div className="relative flex min-h-0 flex-1 flex-col">
                    <div
                        ref={scroll.ref}
                        className="scrollbar-hide min-h-0 flex-1 overflow-y-auto"
                    >
                        <div
                            ref={bodyRef}
                            className={cn(
                                // `pb-6` is what a step's sticky footer cancels with
                                // `-mb-6` to reach the sheet's bottom edge; keep the
                                // two in step.
                                'flex flex-col px-6 pt-2 pb-6',
                                bodyClassName,
                            )}
                        >
                            {children}
                        </div>
                        {/* Last inside the scroller — see the surface. The
                            drawer mounts its content after this hook runs,
                            which the callback ref is what handles. */}
                        <div
                            className="h-px"
                            ref={scroll.sentinelRef}
                            aria-hidden
                        />
                    </div>
                    <ScrollHint
                        show={scroll.showHint}
                        className={scrollHintClassName}
                    />
                </div>
            </DrawerContent>
        </Drawer>
    );
};

/**
 * Tracks the height the sheet needs to fit the current step exactly.
 *
 * A multi-step sheet has two bad ends: hug the content and the surface snaps to
 * a new size on every step, or hold one height and every short step carries a
 * pool of dead space. The usual answer is neither — take the content's height,
 * and animate the change. `height: auto` cannot be transitioned (and
 * `interpolate-size` is not in Safari, which is most of this audience), so the
 * height has to be a measured number.
 *
 * The body is measured inside the scroll container, where it is free to be its
 * natural height, and the drag handle is measured alongside it rather than
 * assumed — together they are the whole surface. Watching both with a
 * ResizeObserver keeps the number honest when the step has not changed but its
 * content has: a validation message appearing, an accordion opening.
 *
 * `animate` stays false for the first measurement so the sheet does not grow
 * from nothing on open — that entrance belongs to vaul's slide-up.
 */
const useSheetContentHeight = (open: boolean, keyboardMoving: boolean) => {
    // Callback refs, not `useRef`: the drawer mounts through a portal whose
    // content lands after this component's layout effect, so a ref object is
    // still null at the only moment a mount-keyed effect would read it. These
    // fire when the nodes actually attach.
    const [handle, handleRef] = React.useState<HTMLDivElement | null>(null);
    const [body, bodyRef] = React.useState<HTMLDivElement | null>(null);
    const [contentHeight, setContentHeight] = React.useState<number | null>(
        null,
    );
    const [animate, setAnimate] = React.useState(false);

    React.useLayoutEffect(() => {
        if (!open || !body) {
            setContentHeight(null);
            setAnimate(false);
            return;
        }

        const commit = () => {
            const next = (handle?.offsetHeight ?? 0) + body.offsetHeight;
            // Zero means nothing has been laid out to measure yet. Committing
            // it would collapse the sheet; leaving it null keeps the drawer's
            // own `h-auto` until there is a real number.
            setContentHeight(next > 0 ? next : null);
        };

        // Coalesced to one commit per frame, always reading the latest layout.
        //
        // A step swap does not resize the body once. Measured on the auth modal,
        // moving from the email step to the details step reports 72px, 72px,
        // then 515px in the same swap: the outgoing step is gone and the
        // incoming one has not laid out yet. Committing that first number sets
        // the sheet to a fraction of its height and springs it back out — the
        // bounce, arriving as a collapse rather than as motion. React happens to
        // batch those three into one render on a fast machine, which is why it
        // survives here and not on a phone; a frame is a guarantee where a batch
        // is luck.
        let frame = 0;
        const measure = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                commit();
            });
        };
        commit();

        const observer =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(measure)
                : null;
        observer?.observe(body);
        if (handle) observer?.observe(handle);

        return () => {
            if (frame) cancelAnimationFrame(frame);
            observer?.disconnect();
        };
    }, [open, body, handle]);

    // One frame after the first measurement, so the opening sheet is not also
    // animating its height from zero.
    React.useEffect(() => {
        if (contentHeight === null || animate || prefersReducedMotion()) return;
        const frame = requestAnimationFrame(() => setAnimate(true));
        return () => cancelAnimationFrame(frame);
    }, [contentHeight, animate]);

    // Carry the incoming step in over the front of the resize. Without it the
    // new step is fully painted at its final position while the box around it
    // is still travelling, which is the part that reads as a jump however
    // smoothly the height itself moves.
    //
    // Read through a ref rather than a dependency. `keyboardMoving` flips a few
    // hundred milliseconds after the step that focuses an input commits — right
    // in the middle of this 260ms carry-in — and as a dependency it would tear
    // the effect down mid-flight. `cancel()` drops a running animation's effect
    // on the spot, so the step would snap from half-faded to placed. That snap
    // is the glitch it is meant to prevent.
    const keyboardMovingRef = React.useRef(keyboardMoving);
    React.useEffect(() => {
        keyboardMovingRef.current = keyboardMoving;
    }, [keyboardMoving]);

    const previousHeight = React.useRef<number | null>(null);
    React.useEffect(() => {
        const previous = previousHeight.current;
        previousHeight.current = contentHeight;
        if (!animate || !body || previous === null || contentHeight === null) {
            return;
        }
        if (previous === contentHeight) return;
        // A step change earns the carry-in. A form reflowing under the keyboard
        // does not — flashing the step every time the keyboard moves is the
        // opposite of settling it.
        if (keyboardMovingRef.current) return;
        // jsdom has no Web Animations API, and neither does every browser we
        // still serve; the step is simply already in place without it.
        if (typeof body.animate !== 'function') return;

        const animation = body.animate(
            [
                { opacity: 0.55, transform: 'translateY(6px)' },
                { opacity: 1, transform: 'translateY(0)' },
            ],
            {
                duration: CONTENT_SETTLE_MS,
                easing: 'cubic-bezier(0.32, 0.72, 0, 1)',
            },
        );
        return () => animation.cancel();
    }, [contentHeight, animate, body]);

    return { handleRef, bodyRef, contentHeight, animate };
};

/**
 * True from the first viewport change until it has been quiet for a moment.
 *
 * The keyboard does not arrive in one step — it reports its height on most
 * frames of its own opening animation, and again on close, and again when the
 * page scrolls under it. Each of those is a real geometry change the sheet must
 * follow immediately; none of them is a step change worth springing.
 */
const useKeyboardMoving = (offset: number) => {
    const [moving, setMoving] = React.useState(false);
    const previous = React.useRef(offset);

    React.useEffect(() => {
        if (previous.current === offset) return;
        previous.current = offset;
        setMoving(true);
        const timer = setTimeout(() => setMoving(false), KEYBOARD_SETTLE_MS);
        return () => clearTimeout(timer);
    }, [offset]);

    return moving;
};

/**
 * Read at the moment a transition would start rather than subscribed to: the
 * only thing that reacts to a mid-flight change of this setting is the next
 * step, which is soon enough and costs no listener per open sheet.
 */
const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The two-pane modal card: the shared `TwoPaneSurface` in a fixed poster-style
 * dialog, going full-screen on a narrow window. The panes themselves live in
 * that surface, which the `/login` page renders without a dialog around it.
 */
const TwoPaneCard = ({
    open,
    onOpenChange,
    title,
    pane,
    paneImage,
    paneStatement,
    children,
    bodyPadding = 'default',
    corners = 'square',
    scrollHintClassName,
    scrollResetKey,
    closeButton = true,
    closeButtonVariant,
    closeButtonClassName,
    className,
    paneClassName,
    bodyClassName,
    onPointerDownOutside,
}: TwoPaneModalProps) => {
    const rounded = corners === 'rounded';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                variant="default"
                spacing="none"
                closeButton={closeButton}
                closeButtonVariant={closeButtonVariant}
                closeButtonClassName={cn(
                    TWO_PANE_CLOSE_BUTTON_CLASSNAME,
                    closeButtonClassName,
                )}
                className={cn(
                    'block gap-0 overflow-hidden border-0 bg-white p-0',
                    // Below `md` the card is full-screen and must stay square so
                    // the overlay does not show through clipped corners.
                    // `sm:rounded-none` clears Dialog's default `sm:rounded-lg`;
                    // only a same-prefix utility is merged away by `cn`.
                    // At `md+`, `rounded` uses ~20px (Figma ≈20.6); step = px/4 in BM.
                    'rounded-none sm:rounded-none',
                    rounded && 'md:rounded-5',
                    'max-md:inset-0 max-md:top-0 max-md:left-0 max-md:h-dvh max-md:max-w-full max-md:translate-x-0 max-md:translate-y-0',
                    TWO_PANE_SURFACE_SIZE_CLASSNAME,
                    className,
                )}
                onPointerDownOutside={onPointerDownOutside}
            >
                <DialogTitle className="sr-only">{title}</DialogTitle>
                <TwoPaneSurface
                    pane={pane}
                    paneImage={paneImage}
                    paneStatement={paneStatement}
                    bodyPadding={bodyPadding}
                    corners={corners}
                    scrollHintClassName={scrollHintClassName}
                    scrollResetKey={scrollResetKey}
                    paneClassName={paneClassName}
                    bodyClassName={bodyClassName}
                >
                    {children}
                </TwoPaneSurface>
            </DialogContent>
        </Dialog>
    );
};
